import React, { forwardRef } from 'react'

import cn from '@/utils/tailwind/cn'

type Props = {
  hasError?: boolean
  onChange?: (event: React.ChangeEvent<HTMLInputElement> | string) => void
  useEvent?: boolean
  grayOutWhenDisabled?: boolean
  value?: string
  id?: string
  name?: string
  inputId?: string | null
  className?: string
  isOfficeUse?: boolean
} & React.InputHTMLAttributes<HTMLInputElement>

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      grayOutWhenDisabled,
      onChange,
      useEvent,
      value: inputValue,
      inputId,
      id,
      name,
      hasError,
      className,
      isOfficeUse,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = React.useState(inputValue || '')

    React.useEffect(() => {
      setValue(inputValue || '')
    }, [inputValue])

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const targetValue = event.target.value
        setValue(targetValue)

        if (onChange) {
          onChange(useEvent ? event : targetValue)
        }
      },
      [onChange, useEvent]
    )

    return (
      <input
        className={cn(
          'form-input block w-full rounded-md border transition focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)]',
          'text-gray-700 duration-150 ease-in-out sm:text-sm sm:leading-5',
          { 'disabled:bg-gray-100': grayOutWhenDisabled || isOfficeUse },
          hasError && 'border-red-500',
          className
        )}
        id={inputId || id || name}
        name={name}
        onChange={handleChange}
        ref={ref}
        value={value}
        {...props}
      />
    )
  }
)
